import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Paperclip, Trash, CheckCircle, CaretUpDown, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const PAY_TYPES = ["Bank Transfer", "NEFT", "RTGS", "IMPS", "UPI", "Cash", "Cheque"];

export default function ExpenseCreate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [centres, setCentres] = useState([]);
  const [cats, setCats] = useState([]);
  const [tdsRates, setTdsRates] = useState({});
  const [vendors, setVendors] = useState([]);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    centre_id: "",
    expense_date: new Date().toISOString().slice(0, 10),
    category: "",
    sub_category: "",
    vendor: "",
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    description: "",
    amount_before_gst: 0,
    gst_pct: 0,
    cgst: 0, sgst: 0, igst: 0, tds_pct: 0, tds: 0,
    payment_type: "Bank Transfer",
    expense_type: "Operational",
    budget_head: "",
    remarks: "",
    attachments: [],
  });

  useEffect(() => {
    api.get("/centres").then((r) => {
      setCentres(r.data);
      // Access rules:
      //  - Admin or all_centres wildcard: pick from full list.
      //  - Exactly 1 assigned centre: auto-select + lock.
      //  - Multiple assigned: user picks from the (already-filtered) list.
      //  - Zero assigned + not wildcard: form is blocked (see render below).
      const hasWildcard = user && (user.role === "admin" || user.all_centres);
      if (hasWildcard) {
        if (r.data.length === 1) setForm((f) => ({ ...f, centre_id: r.data[0].id }));
      } else if (r.data.length === 1) {
        setForm((f) => ({ ...f, centre_id: r.data[0].id }));
      }
    });
    api.get("/meta/categories").then((r) => {
      setCats(r.data.expense);
      setTdsRates(r.data.tds_rates || {});
    });
    api.get("/vendors").then((r) => setVendors(r.data));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => Number(v || 0);
  const round2 = (v) => Math.round(num(v) * 100) / 100;

  // GST auto-split: when user changes GST%, split into CGST/SGST (half each)
  // and clear IGST. If user manually edits CGST/SGST/IGST later, they override.
  const setGstPct = (pct) => {
    const p = num(pct);
    const base = num(form.amount_before_gst);
    const gstAmt = round2(base * p / 100);
    const half = round2(gstAmt / 2);
    setForm((f) => ({ ...f, gst_pct: pct, cgst: half, sgst: gstAmt - half, igst: 0 }));
  };
  const setAmountBase = (v) => {
    // Recompute GST amounts whenever base changes if a % is set
    const base = num(v);
    if (num(form.gst_pct) > 0) {
      const gstAmt = round2(base * num(form.gst_pct) / 100);
      const half = round2(gstAmt / 2);
      setForm((f) => ({ ...f, amount_before_gst: v, cgst: half, sgst: gstAmt - half, igst: 0 }));
    } else {
      set("amount_before_gst", v);
    }
  };

  const gstAmount = round2(num(form.cgst) + num(form.sgst) + num(form.igst));
  const total = round2(num(form.amount_before_gst) + gstAmount);
  const net = round2(total - num(form.tds));

  // Auto-recompute TDS whenever gross total or TDS % changes (only if the user
  // hasn't manually set a TDS amount that differs from the computed one).
  const setTdsPct = (pct) => {
    const p = num(pct);
    const tdsAmt = round2(total * p / 100);
    setForm((f) => ({ ...f, tds_pct: pct, tds: tdsAmt }));
  };

  // When the user picks a category, preset the TDS % from the map (server-provided).
  // If the user has already set a manual TDS %, we DON'T override — we only
  // populate the default when TDS is still 0/blank.
  const setCategory = (cat) => {
    const suggested = tdsRates[cat];
    if (suggested != null && (!form.tds_pct || Number(form.tds_pct) === 0)) {
      const tdsAmt = round2(total * suggested / 100);
      setForm((f) => ({ ...f, category: cat, tds_pct: suggested, tds: tdsAmt }));
    } else {
      set("category", cat);
    }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    set("attachments", [...form.attachments, r.data]);
    toast.success(`Uploaded ${file.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.centre_id) return toast.error("Please select a centre");
    if (!form.category) return toast.error("Category required");
    if (!form.description?.trim()) return toast.error("Description is mandatory.");
    setBusy(true);
    try {
      const payload = { ...form, total_amount: total, net_payable: net };
      const r = await api.post("/expenses", payload);
      toast.success(`Expense ${r.data.expense_no} submitted`);
      nav(`/expenses/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hasWildcard = user && (user.role === "admin" || user.all_centres);
  const assignedIds = hasWildcard ? centres.map((c) => c.id) : (user?.centre_ids || []);
  const managerLocked = !hasWildcard && assignedIds.length === 1 && centres.length === 1;
  const lockedCentre = managerLocked ? centres[0] : null;
  const zeroAccess = !hasWildcard && assignedIds.length === 0;

  if (zeroAccess) {
    return (
      <div className="p-8 max-w-2xl" data-testid="expense-create-blocked">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Access</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">Cannot Create Expense</h1>
        <div className="mt-8 bg-amber-50 border border-amber-300 p-6 rounded-sm">
          <p className="text-sm text-amber-900">
            No centre has been assigned to your account. Please contact your administrator to assign one before submitting expenses.
          </p>
        </div>
        <div className="mt-6">
          <Button type="button" variant="outline" onClick={() => nav("/expenses")} className="rounded-sm h-10 border-zinc-300">
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl" data-testid="expense-create-root">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">New Request</div>
      <h1 className="font-heading text-4xl mt-1 tracking-tight">Create Expense</h1>
      <p className="text-sm text-zinc-500 mt-1">Submit for Head Office approval.</p>

      <form onSubmit={submit} className="mt-8 space-y-8">
        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">01 · Centre & Category</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">
                Centre {managerLocked && <span className="ml-2 text-[10px] tracking-widest bg-zinc-100 text-zinc-700 border border-zinc-300 px-1.5 py-0.5">LOCKED</span>}
              </Label>
              {managerLocked ? (
                <div
                  className="mt-2 h-10 border border-zinc-300 bg-zinc-50 rounded-sm px-3 flex items-center justify-between text-sm"
                  data-testid="expense-centre-locked"
                >
                  <span className="font-medium">
                    {lockedCentre ? `${lockedCentre.code} · ${lockedCentre.name}` : "Loading…"}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-assigned</span>
                </div>
              ) : (
                <Select value={form.centre_id} onValueChange={(v) => set("centre_id", v)}>
                  <SelectTrigger className="rounded-sm h-10 mt-2 border-zinc-300" data-testid="expense-centre">
                    <SelectValue placeholder="Select centre" />
                  </SelectTrigger>
                  <SelectContent>
                    {centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Expense Date</Label>
              <Input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" data-testid="expense-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Category</Label>
              <Select value={form.category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-sm h-10 mt-2 border-zinc-300" data-testid="expense-category">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Sub-category</Label>
              <Input value={form.sub_category} onChange={(e) => set("sub_category", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Budget Head</Label>
              <Input value={form.budget_head} onChange={(e) => set("budget_head", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Expense Type</Label>
              <Select value={form.expense_type} onValueChange={(v) => set("expense_type", v)}>
                <SelectTrigger className="rounded-sm h-10 mt-2 border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Operational">Operational</SelectItem>
                  <SelectItem value="Capital">Capital</SelectItem>
                  <SelectItem value="Recurring">Recurring</SelectItem>
                  <SelectItem value="One-time">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">02 · Vendor & Invoice</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Vendor / Supplier</Label>
              <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={vendorOpen}
                    data-testid="expense-vendor"
                    className="w-full h-10 mt-2 border border-zinc-300 rounded-sm px-3 flex items-center justify-between text-sm hover:bg-zinc-50 bg-white"
                  >
                    <span className={form.vendor ? "text-zinc-900" : "text-zinc-400"}>
                      {form.vendor || "Select or type vendor…"}
                    </span>
                    <CaretUpDown size={14} className="text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 rounded-sm bg-white" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                  <Command>
                    <CommandInput
                      placeholder="Search vendors…"
                      value={form.vendor}
                      onValueChange={(v) => set("vendor", v)}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-3 text-xs">
                          No vendor found. Type to use "<span className="font-medium">{form.vendor}</span>" as a free-text vendor.
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {vendors.map((v) => (
                          <CommandItem
                            key={v.id}
                            value={v.name}
                            onSelect={() => { set("vendor", v.name); setVendorOpen(false); }}
                            data-testid={`vendor-opt-${v.name.replace(/\s/g,"-").toLowerCase()}`}
                          >
                            <Check size={14} className={`mr-2 ${form.vendor === v.name ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex-1">
                              <div className="text-sm">{v.name}</div>
                              <div className="text-xs text-zinc-500 font-mono">{v.gstin || "No GSTIN"} · {v.category || ""}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Invoice / Bill No</Label>
              <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" data-testid="expense-invoice" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-widest">Description / Purpose <span className="text-rose-600">*</span></Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                required
                data-testid="expense-description"
                        rows={2} className="rounded-sm mt-2 border-zinc-300" />
            </div>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">03 · Amount & Taxes</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Amount (Before GST)</Label>
              <Input
                type="number" step="0.01" value={form.amount_before_gst}
                onChange={(e) => setAmountBase(e.target.value)}
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-amount_before_gst"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">GST %</Label>
              <Input
                type="number" step="0.01" value={form.gst_pct}
                onChange={(e) => setGstPct(e.target.value)}
                placeholder="e.g. 18"
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-gst_pct"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Auto-splits into CGST + SGST</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">GST Amount</Label>
              <Input
                type="number" value={gstAmount} readOnly
                className="rounded-sm h-10 mt-2 border-zinc-200 bg-zinc-50 tabular-nums text-right"
                data-testid="expense-gst-amount"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">CGST</Label>
              <Input
                type="number" step="0.01" value={form.cgst}
                onChange={(e) => set("cgst", e.target.value)}
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-cgst"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">SGST</Label>
              <Input
                type="number" step="0.01" value={form.sgst}
                onChange={(e) => set("sgst", e.target.value)}
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-sgst"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">IGST</Label>
              <Input
                type="number" step="0.01" value={form.igst}
                onChange={(e) => set("igst", e.target.value)}
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-igst"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">TDS %</Label>
              <Input
                type="number" step="0.01" value={form.tds_pct}
                onChange={(e) => setTdsPct(e.target.value)}
                placeholder="e.g. 2 / 10"
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-tds_pct"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Auto-calculates TDS from gross</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">TDS Amount</Label>
              <Input
                type="number" step="0.01" value={form.tds}
                onChange={(e) => set("tds", e.target.value)}
                className="rounded-sm h-10 mt-2 border-zinc-300 tabular-nums text-right"
                data-testid="expense-tds"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Deducted from gross to arrive at Net Payable</div>
            </div>
          </div>
          {/* Clean summary: Subtotal → GST → Gross → TDS → Net Payable */}
          <div className="pt-4 mt-2 border-t border-zinc-200 space-y-1 text-sm tabular-nums" data-testid="expense-totals">
            <div className="flex justify-between text-zinc-600">
              <span>Amount (before GST)</span>
              <span data-testid="expense-subtotal">{fmtINR(num(form.amount_before_gst))}</span>
            </div>
            <div className="flex justify-between text-zinc-600">
              <span>GST (CGST + SGST + IGST)</span>
              <span>+ {fmtINR(gstAmount)}</span>
            </div>
            <div className="flex justify-between font-medium text-zinc-900 border-t border-zinc-100 pt-1">
              <span>Gross Total (incl. GST)</span>
              <span data-testid="expense-total">{fmtINR(total)}</span>
            </div>
            <div className="flex justify-between text-rose-700">
              <span>Less: TDS {form.tds_pct ? `(${form.tds_pct}%)` : ""}</span>
              <span>− {fmtINR(num(form.tds))}</span>
            </div>
            <div className="flex justify-between font-heading text-lg text-emerald-800 border-t-2 border-zinc-900 pt-2 mt-2">
              <span>Net Payable</span>
              <span data-testid="expense-net">{fmtINR(net)}</span>
            </div>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">04 · Bills & Supporting Documents</div>
          <label className="border-2 border-dashed border-zinc-300 rounded-sm p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-zinc-50">
            <Paperclip size={20} />
            <div className="text-sm">Click to upload PDF, JPG, PNG, Excel</div>
            <div className="text-xs text-zinc-500">Max 15MB per file</div>
            <input type="file" className="hidden" data-testid="expense-file-input"
                   accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
                   onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
          {form.attachments.length > 0 && (
            <div className="space-y-1">
              {form.attachments.map((a, i) => (
                <div key={a.id} className="flex justify-between items-center px-3 py-2 bg-zinc-50 border border-zinc-200 text-sm">
                  <span className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-600" />{a.original_filename}</span>
                  <button type="button" onClick={() => set("attachments", form.attachments.filter((_, j) => j !== i))}>
                    <Trash size={14} className="text-zinc-400 hover:text-rose-600" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">05 · Payment Preference & Remarks</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Preferred Payment Type</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                <SelectTrigger className="rounded-sm h-10 mt-2 border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Remarks</Label>
              <Input value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
                     className="rounded-sm h-10 mt-2 border-zinc-300" />
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav("/expenses")}
                  className="rounded-sm h-10 border-zinc-300">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="submit-expense"
                  className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10">
            {busy ? "Submitting…" : "Submit for Approval"}
          </Button>
        </div>
      </form>
    </div>
  );
}
